// Famous ecommerce brand catalog for the "Shop by Brand" section.
// All outbound links are auto-monetized by the Cuelinks snippet in index.html
// (when the merchant is supported). Icons are AI-generated abstract marks
// inspired by each brand — they are not the official logos.

import amazonIcon from "@/assets/brands/amazon.webp";
import flipkartIcon from "@/assets/brands/flipkart.webp";
import myntraIcon from "@/assets/brands/myntra.webp";
import ajioIcon from "@/assets/brands/ajio.webp";
import nykaaIcon from "@/assets/brands/nykaa.webp";
import tatacliqIcon from "@/assets/brands/tatacliq.webp";
import snapdealIcon from "@/assets/brands/snapdeal.webp";
import asosIcon from "@/assets/brands/asos.webp";
import hmIcon from "@/assets/brands/hm.webp";
import nordstromIcon from "@/assets/brands/nordstrom.webp";
import macysIcon from "@/assets/brands/macys.webp";
import targetIcon from "@/assets/brands/target.webp";
import nextIcon from "@/assets/brands/next.webp";
import boohooIcon from "@/assets/brands/boohoo.webp";
import pltIcon from "@/assets/brands/plt.webp";
import noonIcon from "@/assets/brands/noon.webp";
import namshiIcon from "@/assets/brands/namshi.webp";

export type Country = "IN" | "US" | "UK" | "AE" | "Global";

export interface Brand {
  id: string;
  name: string;
  tagline: string;
  // Tailwind gradient classes for the tile background
  gradient: string;
  // AI-generated abstract icon (transparent PNG)
  icon: string;
  // Per-country storefront URLs. Falls back to "Global" key.
  urls: Partial<Record<Country, string>>;
  // Countries this brand actually serves (drives the country filter)
  countries: Country[];
}

export const COUNTRIES: { code: Country; label: string; flag: string }[] = [
  { code: "IN", label: "India", flag: "🇮🇳" },
  { code: "US", label: "USA", flag: "🇺🇸" },
  { code: "UK", label: "UK", flag: "🇬🇧" },
  { code: "AE", label: "UAE", flag: "🇦🇪" },
  { code: "Global", label: "Global", flag: "🌍" },
];

export const BRANDS: Brand[] = [
  // ── India ──────────────────────────────────────────────
  {
    id: "amazon",
    name: "Amazon",
    tagline: "Everything store",
    gradient: "from-orange-500 to-amber-600",
    icon: amazonIcon,
    countries: ["IN", "US", "UK", "AE"],
    urls: {
      IN: "https://www.amazon.in/s?k=fashion",
      US: "https://www.amazon.com/s?k=fashion",
      UK: "https://www.amazon.co.uk/s?k=fashion",
      AE: "https://www.amazon.ae/s?k=fashion",
    },
  },
  {
    id: "flipkart",
    name: "Flipkart",
    tagline: "India's marketplace",
    gradient: "from-blue-500 to-yellow-500",
    icon: flipkartIcon,
    countries: ["IN"],
    urls: { IN: "https://www.flipkart.com/clothing-and-accessories/pr?sid=clo" },
  },
  {
    id: "myntra",
    name: "Myntra",
    tagline: "Fashion-first",
    gradient: "from-pink-500 to-rose-600",
    icon: myntraIcon,
    countries: ["IN"],
    urls: { IN: "https://www.myntra.com/shop/men,women" },
  },
  {
    id: "ajio",
    name: "Ajio",
    tagline: "Curated styles",
    gradient: "from-fuchsia-600 to-purple-700",
    icon: ajioIcon,
    countries: ["IN"],
    urls: { IN: "https://www.ajio.com/shop/sale" },
  },
  {
    id: "nykaa",
    name: "Nykaa Fashion",
    tagline: "Beauty & fashion",
    gradient: "from-pink-600 to-red-500",
    icon: nykaaIcon,
    countries: ["IN"],
    urls: { IN: "https://www.nykaafashion.com/" },
  },
  {
    id: "tatacliq",
    name: "Tata CLiQ",
    tagline: "Premium picks",
    gradient: "from-indigo-700 to-blue-600",
    icon: tatacliqIcon,
    countries: ["IN"],
    urls: { IN: "https://www.tatacliq.com/" },
  },
  {
    id: "snapdeal",
    name: "Snapdeal",
    tagline: "Daily deals",
    gradient: "from-red-500 to-orange-500",
    icon: snapdealIcon,
    countries: ["IN"],
    urls: { IN: "https://www.snapdeal.com/" },
  },

  // ── USA ────────────────────────────────────────────────
  {
    id: "nordstrom",
    name: "Nordstrom",
    tagline: "Luxury department",
    gradient: "from-slate-700 to-slate-900",
    icon: nordstromIcon,
    countries: ["US"],
    urls: { US: "https://www.nordstrom.com/" },
  },
  {
    id: "macys",
    name: "Macy's",
    tagline: "American classic",
    gradient: "from-red-700 to-rose-900",
    icon: macysIcon,
    countries: ["US"],
    urls: { US: "https://www.macys.com/" },
  },
  {
    id: "target",
    name: "Target",
    tagline: "Everyday style",
    gradient: "from-red-600 to-rose-600",
    icon: targetIcon,
    countries: ["US"],
    urls: { US: "https://www.target.com/c/clothing" },
  },

  // ── UK ─────────────────────────────────────────────────
  {
    id: "next",
    name: "Next",
    tagline: "British essentials",
    gradient: "from-emerald-700 to-teal-700",
    icon: nextIcon,
    countries: ["UK"],
    urls: { UK: "https://www.next.co.uk/" },
  },
  {
    id: "boohoo",
    name: "Boohoo",
    tagline: "Trend-led fashion",
    gradient: "from-pink-500 to-fuchsia-600",
    icon: boohooIcon,
    countries: ["UK"],
    urls: { UK: "https://www.boohoo.com/" },
  },
  {
    id: "plt",
    name: "PrettyLittleThing",
    tagline: "Bold party looks",
    gradient: "from-pink-400 to-rose-500",
    icon: pltIcon,
    countries: ["UK"],
    urls: { UK: "https://www.prettylittlething.com/" },
  },

  // ── UAE ────────────────────────────────────────────────
  {
    id: "noon",
    name: "Noon",
    tagline: "Middle East market",
    gradient: "from-yellow-500 to-amber-500",
    icon: noonIcon,
    countries: ["AE"],
    urls: { AE: "https://www.noon.com/uae-en/fashion/" },
  },
  {
    id: "namshi",
    name: "Namshi",
    tagline: "Fashion in the UAE",
    gradient: "from-teal-600 to-cyan-700",
    icon: namshiIcon,
    countries: ["AE"],
    urls: { AE: "https://en-ae.namshi.com/" },
  },

  // ── Global / Multi-region ──────────────────────────────
  {
    id: "asos",
    name: "ASOS",
    tagline: "Trend-forward",
    gradient: "from-zinc-800 to-zinc-950",
    icon: asosIcon,
    countries: ["UK", "US", "Global"],
    urls: {
      UK: "https://www.asos.com/",
      US: "https://www.asos.com/us/",
      Global: "https://www.asos.com/",
    },
  },
  {
    id: "hm",
    name: "H&M",
    tagline: "Affordable fashion",
    gradient: "from-red-600 to-rose-700",
    icon: hmIcon,
    countries: ["IN", "US", "UK", "AE", "Global"],
    urls: {
      IN: "https://www2.hm.com/en_in/index.html",
      US: "https://www2.hm.com/en_us/index.html",
      UK: "https://www2.hm.com/en_gb/index.html",
      AE: "https://www2.hm.com/en_ae/index.html",
      Global: "https://www2.hm.com/",
    },
  },
];

/** Get the storefront URL for a brand in the chosen country with fallback. */
export function brandUrl(brand: Brand, country: Country): string | null {
  return (
    brand.urls[country] ??
    brand.urls.Global ??
    brand.urls.IN ??
    brand.urls.US ??
    Object.values(brand.urls)[0] ??
    null
  );
}

/**
 * Detect the merchant brand from a product URL (used for the Result page CTA).
 * Returns the matching Brand or null.
 */
export function detectBrandFromUrl(url: string | null | undefined): Brand | null {
  if (!url) return null;
  let host = "";
  try {
    host = new URL(url).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return null;
  }
  const map: Record<string, string> = {
    "amazon.in": "amazon",
    "amazon.com": "amazon",
    "amazon.co.uk": "amazon",
    "amazon.ae": "amazon",
    "flipkart.com": "flipkart",
    "myntra.com": "myntra",
    "ajio.com": "ajio",
    "nykaafashion.com": "nykaa",
    "nykaa.com": "nykaa",
    
    "tatacliq.com": "tatacliq",
    "snapdeal.com": "snapdeal",
    "nordstrom.com": "nordstrom",
    "macys.com": "macys",
    "target.com": "target",
    "next.co.uk": "next",
    "boohoo.com": "boohoo",
    "prettylittlething.com": "plt",
    "noon.com": "noon",
    "namshi.com": "namshi",
    "en-ae.namshi.com": "namshi",
    "asos.com": "asos",
    
    "hm.com": "hm",
    "www2.hm.com": "hm",
  };
  for (const key of Object.keys(map)) {
    if (host === key || host.endsWith("." + key)) {
      return BRANDS.find((b) => b.id === map[key]) ?? null;
    }
  }
  return null;
}
